<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { round } from "lodash";
import { Dialog } from "../../../../base-components/Headless";
import Button from "../../../../base-components/Button";
import {
  FormLabel,
  FormInput,
  FormTextarea,
  FormCheck
} from "../../../../base-components/Form";
import TomSelect from "../../../../custom-components/TomSelect/TomSelect.vue";
import InputNumero from "../../../../custom-components/InputNumero/InputNumero.vue";
import { useProductosCategoriasStore } from "../../../../services/productosCategorias";
import { useTasasIVAStore } from "../../../../services/tasas";
import { userUserStore } from "../../../../stores/user";
import { numeroFormateado } from "../../../../utils/helper";
import { useMonedasStore } from "../../../../services/monedas";
import FormSelect from "../../../../base-components/Form/FormSelect.vue";

const props = defineProps<{
  cargando?: boolean;
}>();

export type AgregarProductoManualPayload = {
  nombre: string;
  categoriaId: string;
  categoriaNombre: string;
  precioUnitarioSinIva: number;
  precioUnitarioConIva: number;
  tasaValor: number;
  cantidad: number;
  observaciones: string;
  impresionSectores: string[];
  tasaIvaId: string;
};

const categoriasStore = useProductosCategoriasStore();
const tasasStore = useTasasIVAStore();
const userStore = userUserStore();
const errorFormulario = ref("");
const monedasStore = useMonedasStore();

const emit = defineEmits<{
  (e: "cerrar"): void;
  (e: "agregar", payload: AgregarProductoManualPayload): void;
}>();

const cantidad = ref(1);

const form = reactive({
  nombre: "",
  categoriaId: userStore.opcionesGenerales.categoriaDefectoManual ?? "",
  tasaId: "",
  precioSinIva: 0,
  precioConIva: 0,
  observaciones: "",
  impresionSectores: [] as string[]
});

const sectoresImpresionLista = computed(() => {
  const og = userStore.opcionesGenerales as {
    sectores?: string[];
    sectoresImpresion?: string[];
  };
  return og.sectoresImpresion?.length
    ? og.sectoresImpresion
    : (og.sectores ?? []);
});

const tasaSeleccionada = computed(() =>
  tasasStore.getTasaIVAById(form.tasaId ?? "")
);

const totalLineaPreview = computed(() => {
  const u = Number(form.precioConIva) || 0;
  return round(u * cantidad.value, 2);
});

const categoriasActivasOrdenadas = computed(() => {
  return categoriasStore.categorias
    .filter(c => c.activo)
    .sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es", { sensitivity: "base" })
    );
});

onMounted(() => {
  const defTasa = userStore.opcionesGenerales.tasaIVADefecto;
  if (defTasa && tasasStore.getTasaIVAById(defTasa)) {
    form.tasaId = defTasa;
  } else {
    const first = tasasStore.tasaActivas[0];
    if (first) form.tasaId = first.id;
  }
});

watch(
  () => categoriasActivasOrdenadas.value,
  categorias => {
    // Si la categoría seleccionada deja de estar activa/disponible, limpiamos.
    if (
      form.categoriaId &&
      !categorias.some(categoria => categoria.id === form.categoriaId)
    ) {
      form.categoriaId = "";
    }
  },
  { immediate: true }
);

const changePrecioTasa = (precioSinIva: number) => {
  const tasa = tasasStore.getTasaIVAById(form.tasaId);
  if (tasa != null && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    form.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioConIva = (precioSinIva: number) => {
  form.precioSinIva = precioSinIva;
  const tasa = tasasStore.getTasaIVAById(form.tasaId);
  if (tasa && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    form.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioSinIva = (precioConIva: number) => {
  form.precioConIva = precioConIva;
  const tasa = tasasStore.getTasaIVAById(form.tasaId);
  if (tasa != null && !isNaN(precioConIva) && !isNaN(tasa.valor)) {
    form.precioSinIva = round(precioConIva / (1 + tasa.valor / 100), 2);
  }
};

const incrementar = () => {
  cantidad.value += 1;
};

const decrementar = () => {
  if (cantidad.value <= 1) return;
  cantidad.value -= 1;
};

const validarYAgregar = () => {
  if (props.cargando) return;
  errorFormulario.value = "";
  const nombre = form.nombre.trim();
  if (!nombre) {
    errorFormulario.value = "Ingresá el nombre del producto.";
    return;
  }
  if (!form.categoriaId) {
    errorFormulario.value = "Seleccioná una categoría.";
    return;
  }
  if (!form.tasaId) {
    errorFormulario.value = "Seleccioná una tasa de IVA.";
    return;
  }

  const tasa = tasasStore.getTasaIVAById(form.tasaId);
  if (!tasa || isNaN(tasa.valor)) {
    errorFormulario.value = "La tasa de IVA seleccionada no es válida.";
    return;
  }

  let sin = Number(form.precioSinIva) || 0;
  let con = Number(form.precioConIva) || 0;
  const rate = 1 + tasa.valor / 100;

  if (sin > 0 && (con === 0 || isNaN(con))) {
    con = round(sin * rate, 2);
    form.precioConIva = con;
  } else if (con > 0 && (sin === 0 || isNaN(sin))) {
    sin = round(con / rate, 2);
    form.precioSinIva = sin;
  }

  if (sin <= 0 || con <= 0) {
    errorFormulario.value =
      "Ingresá el precio sin IVA o con IVA (el otro se calcula solo).";
    return;
  }

  const categoriaNombre =
    categoriasStore.getCategoriaById(form.categoriaId)?.nombre ?? "";

  emit("agregar", {
    nombre,
    categoriaId: form.categoriaId,
    categoriaNombre,
    precioUnitarioSinIva: sin,
    precioUnitarioConIva: con,
    tasaValor: tasa.valor,
    cantidad: cantidad.value,
    observaciones: form.observaciones.trim(),
    impresionSectores: [...form.impresionSectores],
    tasaIvaId: tasa.id
  });
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="lg">
    <Dialog.Panel class="overflow-hidden flex flex-col max-h-[90vh]">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">Producto manual</h2>
      </Dialog.Title>

      <Dialog.Description class="overflow-y-auto">
        <div class="flex flex-col gap-4 pr-1">
          <div
            v-if="errorFormulario"
            class="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-danger text-sm"
          >
            {{ errorFormulario }}
          </div>

          <div>
            <FormLabel class="mb-1.5 block">Nombre</FormLabel>
            <FormInput
              v-model.trim="form.nombre"
              type="text"
              placeholder="Nombre del ítem"
            />
          </div>

          <div>
            <FormLabel class="mb-1.5 block">Categoría</FormLabel>
            <FormSelect
              v-model="form.categoriaId"
              :options="{
                placeholder: 'Seleccioná categoría',
                allowEmptyOption: true
              }"
            >
              <option value="" selected>Seleccioná categoría</option>
              <option
                v-for="rubro in categoriasActivasOrdenadas"
                :key="rubro.id"
                :value="rubro.id"
              >
                {{ rubro.nombre }}
              </option>
            </FormSelect>
          </div>

          <div
            v-if="sectoresImpresionLista.length"
            class="rounded-xl border border-slate-200 bg-slate-50/80 p-3"
          >
            <FormLabel class="mb-2 block text-sm"
              >Sectores de impresión</FormLabel
            >
            <div class="flex flex-wrap gap-3">
              <FormCheck
                v-for="sector in sectoresImpresionLista"
                :key="sector"
                class="flex items-center"
              >
                <FormCheck.Input
                  type="checkbox"
                  :id="`manual-sector-${sector}`"
                  :value="sector"
                  v-model="form.impresionSectores"
                />
                <FormCheck.Label :for="`manual-sector-${sector}`" class="ml-1">
                  {{ sector }}
                </FormCheck.Label>
              </FormCheck>
            </div>
            <p class="mt-2 text-[11px] text-slate-500">
              Opcional: ninguno, uno o varios.
            </p>
          </div>

          <div class="gap-2 sm:grid-cols-2">
            <FormLabel class="mb-1.5 block"
              >Precio (unit.)
              {{
                monedasStore.getMonedaById(
                  userStore.opcionesGenerales.monedaDefecto
                )?.simbolo
              }}
            </FormLabel>
            <InputNumero
              v-model:valor="form.precioConIva"
              @change="(v: number) => changePrecioSinIva(v)"
              :precision="2"
              :align="`left`"
              :autoSeleccionar="true"
              placeholder="Precio unit con iva"
            />
          </div>

          <div class="flex items-center gap-3">
            <FormLabel class="mb-0 shrink-0">Cantidad</FormLabel>
            <button
              type="button"
              class="h-9 w-9 rounded-full bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
              @click="decrementar"
            >
              -
            </button>
            <div class="min-w-10 text-center text-lg font-semibold">
              {{ cantidad }}
            </div>
            <button
              type="button"
              class="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200"
              @click="incrementar"
            >
              +
            </button>
            <span class="ml-auto text-sm text-slate-600">
              Total línea:
              <span class="font-semibold text-primary">
                ${{ numeroFormateado(totalLineaPreview, 2) }}
              </span>
            </span>
          </div>

          <div>
            <FormLabel class="mb-1.5 block">Notas</FormLabel>
            <FormTextarea
              v-model="form.observaciones"
              rows="4"
              maxlength="500"
              placeholder="Información adicional..."
              class="min-h-[96px]"
            />
            <div class="mt-1 text-right text-[11px] text-slate-500">
              {{ form.observaciones.length }}/500
            </div>
          </div>
        </div>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex w-full gap-3">
          <div class="flex-1">
            <Button
              variant="secondary"
              class="w-full"
              @click="() => emit('cerrar')"
            >
              Cancelar
            </Button>
          </div>
          <div class="flex-1">
            <Button
              variant="primary"
              class="w-full"
              :disabled="props.cargando"
              @click="validarYAgregar"
            >
              <span
                v-if="props.cargando"
                class="inline-flex items-center justify-center gap-2"
              >
                <span
                  class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-slate-200 border-t-transparent"
                />
                Agregando...
              </span>
              <span v-else>Agregar al pedido</span>
            </Button>
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
